import { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button } from "../../../components";
import { FormSelect } from "../../../components/forms";
import { Spinner } from "../../../components/loaders";
import { ErrorMessage, ErrorMessageWrapper } from "../../../components/messages";
import { useClientSetContext } from "../hooks/ClientSetContext";
import ThemedModal from "../../../components/utilities/ThemedModal";

const SelectClientSetModalBody = ({
    isFetching,
    isError,
    error,
    refetch,
    filteredSets,
    value,
    handleSelect
}) => {
    if (isFetching)
        return <Spinner className="m-auto" />;

    if (!isFetching && isError)
        return <ErrorMessageWrapper>
            <ErrorMessage
                message={error?.data?.message ?? error?.data ?? "Failed to load client sets."}
                retryCallback={refetch}
            />
        </ErrorMessageWrapper>;

    if (!isFetching && !isError && filteredSets.length === 0)
        return <p>No Client Sets have been created yet.</p>;

    return <FormSelect
        label="Select a Client Set"
        options={filteredSets}
        defaultValue={value}
        onChange={(selection) => handleSelect(selection.value)}
        disableAnimations
    />
}

const SelectClientSetModal = ({ editMode, onSelect, onConfirm = null, ...modalProps }) => {
    const { selectedSetId, clientSets, isFetching, isError, error, refetch } = useClientSetContext();

    const { onHide } = modalProps;
    const [value, setValue] = useState(null);

    // Remove the selected set from the list of client sets if we're in edit mode
    const filteredSets = useMemo(() => {
        if (editMode)
            return clientSets.filter(set => set.value !== selectedSetId);
        return clientSets;
    }, [clientSets, editMode, selectedSetId]);

    // Clear the selected value after onSelect if there's no onConfirm, i.e. the modal is closed on select
    const handleSelect = (selectedValue) => {
        setValue(selectedValue);

        if (onSelect && typeof onSelect === "function")
            onSelect(selectedValue);

        if (!onConfirm || typeof onConfirm !== "function")
            setValue(null);
    }

    return <Modal onHide={onHide} size="md" {...modalProps}>
        <ThemedModal.Header closeButton={onConfirm == null}>
            <Modal.Title>Select Client Set to {editMode?.label ?? "Open"}</Modal.Title>
        </ThemedModal.Header>
        <Modal.Body>
            <SelectClientSetModalBody
                isFetching={isFetching}
                isError={isError}
                error={error}
                refetch={refetch}
                filteredSets={filteredSets}
                value={value}
                handleSelect={handleSelect}
            />
        </Modal.Body>
        {onConfirm && <Modal.Footer>
            <Button variant="success" onClick={() => onConfirm(value)}>Confirm</Button>
            <Button variant="light" onClick={onHide}>Cancel</Button>
        </Modal.Footer>}
    </Modal>
}

export default SelectClientSetModal;